(() => {
  if (customElements.get('image-text-slideshow')) return;

  class ImageTextSlideshow extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.slides = [...this.querySelectorAll('.image-text-slideshow__slide')];
      this.dots = [...this.querySelectorAll('[data-slide-index]')];
      this.controls = this.querySelector('.image-text-slideshow__controls');
      this.liveRegion = this.querySelector('.image-text-slideshow__slides');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.hovered = false;
      this.focused = this.contains(document.activeElement);
      this.blockSelected = false;
      this.touch = null;
      this.animations = [];
      this.setAttribute('data-ready', 'true');
      if (this.controls) this.controls.hidden = false;
      const listen = (target, event, handler) => target.addEventListener(event, handler, { signal });

      listen(this, 'click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.controls?.contains(button)) return;
        if (button.dataset.slideIndex !== undefined) this.show(Number(button.dataset.slideIndex));
        if (button.dataset.action === 'previous') this.show(this.index - 1);
        if (button.dataset.action === 'next') this.show(this.index + 1);
      });
      listen(this, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      });
      listen(this, 'mouseenter', () => { this.hovered = true; this.syncPlayback(); });
      listen(this, 'mouseleave', () => { this.hovered = false; this.syncPlayback(); });
      listen(this, 'focusin', () => { this.focused = true; this.syncPlayback(); });
      listen(this, 'focusout', (event) => {
        this.focused = this.contains(event.relatedTarget);
        this.syncPlayback();
      });
      listen(document, 'visibilitychange', () => this.syncPlayback());
      listen(this.motion, 'change', () => {
        if (this.motion.matches) this.cancelAnimations();
        this.syncPlayback();
      });
      listen(this, 'shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide === event.target || slide.contains(event.target));
        if (index >= 0) { this.blockSelected = true; this.show(index); }
      });
      listen(this, 'shopify:block:deselect', () => { this.blockSelected = false; this.syncPlayback(); });
      listen(this, 'shopify:section:deselect', () => { this.blockSelected = false; this.syncPlayback(); });
      listen(this.liveRegion, 'pointerdown', (event) => {
        if (event.pointerType !== 'touch' || !event.isPrimary) return;
        this.touch = { x: event.clientX, y: event.clientY };
        this.syncPlayback();
      });
      listen(this.liveRegion, 'pointercancel', () => { this.touch = null; this.syncPlayback(); });
      listen(this.liveRegion, 'pointerup', (event) => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x;
        const dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClick = true;
          this.show(this.index + (dx < 0 ? 1 : -1));
          clearTimeout(this.clickTimer);
          this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
        }
        this.syncPlayback();
      });
      this.liveRegion.addEventListener('click', (event) => {
        if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); }
      }, { signal, capture: true });
      this.show(0);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.timer);
      clearTimeout(this.clickTimer);
      this.cancelAnimations();
    }

    cancelAnimations() {
      this.animations?.forEach((animation) => animation.cancel());
      this.animations = [];
    }

    show(index) {
      if (!this.slides.length) return;
      const previousIndex = this.index;
      const direction = index > previousIndex ? 1 : -1;
      this.cancelAnimations();
      this.index = (index + this.slides.length) % this.slides.length;
      const focusedSlide = this.slides.find((slide) => slide.contains(document.activeElement));
      if (focusedSlide && focusedSlide !== this.slides[this.index]) this.dots[this.index]?.focus();
      this.slides.forEach((slide, position) => {
        const active = position === this.index;
        slide.dataset.active = String(active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      if (this.dataset.animation === 'slide' && !this.motion.matches && previousIndex !== this.index) {
        const options = { duration: 450, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)' };
        const frame = (offset) => ({ transform: `translateX(${offset}%)`, opacity: 1, visibility: 'visible' });
        this.animations = [
          this.slides[previousIndex].animate([frame(0), frame(-100 * direction)], options),
          this.slides[this.index].animate([frame(100 * direction), frame(0)], options),
        ];
      }
      this.dots.forEach((dot, position) => dot.setAttribute('aria-current', String(position === this.index)));
      this.syncPlayback();
    }

    syncPlayback() {
      clearTimeout(this.timer);
      const rotating = this.dataset.autoplay === 'true'
        && !this.motion.matches && !this.hovered && !this.focused
        && !this.blockSelected && !this.touch && !document.hidden && this.slides.length > 1;
      this.liveRegion.setAttribute('aria-live', rotating ? 'off' : 'polite');
      if (rotating) this.timer = setTimeout(() => this.show(this.index + 1), Number(this.dataset.interval) || 7000);
    }
  }

  customElements.define('image-text-slideshow', ImageTextSlideshow);
})();
